import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { isTelegram } from '../detector';

const ButtonContext = createContext(null);

/**
 * ButtonProvider abstracts Telegram's MainButton and SecondaryButton
 * In standalone mode, these are rendered as regular React components
 */
export function ButtonProvider({ children }) {
  const [mainButton, setMainButtonState] = useState({
    text: '',
    visible: false,
    loading: false,
    disabled: false,
    color: null,
    textColor: null
  });

  const [secondaryButton, setSecondaryButtonState] = useState({
    text: '',
    visible: false,
    position: 'left'
  });

  // Store click handlers in refs to avoid dependency issues
  const mainClickRef = useRef(null);
  const secondaryClickRef = useRef(null);

  // Sync MainButton with Telegram
  useEffect(() => {
    if (!isTelegram) return;

    const tg = window.Telegram.WebApp;

    // Only show if visible AND has text (Telegram requires non-empty text)
    if (mainButton.visible && mainButton.text) {
      tg.MainButton.setText(mainButton.text);

      if (mainButton.color) {
        tg.MainButton.color = mainButton.color;
      } else {
        tg.MainButton.color = tg.themeParams.button_color;
      }

      if (mainButton.textColor) {
        tg.MainButton.textColor = mainButton.textColor;
      } else {
        tg.MainButton.textColor = tg.themeParams.button_text_color;
      }

      if (mainButton.loading) {
        tg.MainButton.showProgress();
      } else {
        tg.MainButton.hideProgress();
      }

      tg.MainButton.show();
    } else {
      tg.MainButton.hide();
    }
  }, [mainButton.visible, mainButton.text, mainButton.loading, mainButton.color, mainButton.textColor]);

  // Sync SecondaryButton with Telegram
  useEffect(() => {
    if (!isTelegram) return;

    const tg = window.Telegram.WebApp;

    // Only show if visible AND has text (Telegram requires non-empty text)
    if (secondaryButton.visible && secondaryButton.text) {
      // Use setParams for SecondaryButton configuration
      tg.SecondaryButton.setParams({
        text: secondaryButton.text,
        position: secondaryButton.position || 'left',
        is_visible: true
      });
    } else {
      tg.SecondaryButton.hide();
    }
  }, [secondaryButton.visible, secondaryButton.text, secondaryButton.position]);

  // Handle MainButton click events
  useEffect(() => {
    if (!isTelegram) return;

    const tg = window.Telegram.WebApp;

    const handler = () => {
      if (mainClickRef.current) {
        mainClickRef.current();
      }
    };

    tg.MainButton.onClick(handler);
    return () => tg.MainButton.offClick(handler);
  }, []);

  // Handle SecondaryButton click events
  useEffect(() => {
    if (!isTelegram) return;

    const tg = window.Telegram.WebApp;

    const handler = () => {
      if (secondaryClickRef.current) {
        secondaryClickRef.current();
      }
    };

    tg.SecondaryButton.onClick(handler);
    return () => tg.SecondaryButton.offClick(handler);
  }, []);

  /**
   * Configure the main button
   * @param {Object} config - Button configuration
   * @param {string} config.text - Button text
   * @param {boolean} config.visible - Whether button is visible
   * @param {boolean} config.loading - Show loading spinner
   * @param {boolean} config.disabled - Disable button
   * @param {Function} config.onClick - Click handler
   * @param {string} config.color - Button background color
   * @param {string} config.textColor - Button text color
   */
  const setMainButton = useCallback((config) => {
    if (config.onClick !== undefined) {
      mainClickRef.current = config.onClick;
    }
    setMainButtonState(prev => ({
      ...prev,
      text: config.text !== undefined ? config.text : prev.text,
      visible: config.visible !== undefined ? config.visible : prev.visible,
      loading: config.loading !== undefined ? config.loading : prev.loading,
      disabled: config.disabled !== undefined ? config.disabled : prev.disabled,
      color: config.color !== undefined ? config.color : prev.color,
      textColor: config.textColor !== undefined ? config.textColor : prev.textColor
    }));
  }, []);

  /**
   * Configure the secondary button
   */
  const setSecondaryButton = useCallback((config) => {
    if (config.onClick !== undefined) {
      secondaryClickRef.current = config.onClick;
    }
    setSecondaryButtonState(prev => ({
      ...prev,
      text: config.text !== undefined ? config.text : prev.text,
      visible: config.visible !== undefined ? config.visible : prev.visible,
      position: config.position !== undefined ? config.position : prev.position
    }));
  }, []);

  /**
   * Hide all buttons and clear handlers
   */
  const hideAllButtons = useCallback(() => {
    mainClickRef.current = null;
    secondaryClickRef.current = null;
    setMainButtonState({
      text: '',
      visible: false,
      loading: false,
      disabled: false,
      color: null,
      textColor: null
    });
    setSecondaryButtonState({
      text: '',
      visible: false,
      position: 'left'
    });
  }, []);

  /**
   * Show loading state on main button
   */
  const showMainButtonLoading = useCallback(() => {
    setMainButtonState(prev => ({ ...prev, loading: true }));
  }, []);

  /**
   * Hide loading state on main button
   */
  const hideMainButtonLoading = useCallback(() => {
    setMainButtonState(prev => ({ ...prev, loading: false }));
  }, []);

  const value = useMemo(() => ({
    mainButton: {
      ...mainButton,
      onClick: mainClickRef.current
    },
    secondaryButton: {
      ...secondaryButton,
      onClick: secondaryClickRef.current
    },
    setMainButton,
    setSecondaryButton,
    hideAllButtons,
    showMainButtonLoading,
    hideMainButtonLoading,
    isTelegram
  }), [mainButton, secondaryButton, setMainButton, setSecondaryButton, hideAllButtons, showMainButtonLoading, hideMainButtonLoading]);

  return (
    <ButtonContext.Provider value={value}>
      {children}
    </ButtonContext.Provider>
  );
}

/**
 * Hook to access button context
 */
export const useButtons = () => {
  const context = useContext(ButtonContext);
  if (!context) {
    throw new Error('useButtons must be used within a ButtonProvider');
  }
  return context;
};
